import { createSelector } from 'reselect';
import { List, Map, fromJS } from 'immutable';
import walletManager from '../../_core/wallet-manager';
import { getConversionStatusFromMap } from '../../_core/utils';
import { INCENTIVE_MODELS, fSecretFromQuery } from '../../constants';
import { CAMPAIGN_STATUS, CAMPAIGN_TYPES } from '../../constants/campaign';

const inventorySelector = state => state.campaign.getIn(['ipfsCampaignSummary', 'inventory']) || Map();
const isOpenPayItForwardModal = state => state.campaign.get('isOpenPayItForwardModal');
const contractorBalanceSelector = state => state.campaign.getIn(['ipfsCampaignSummary', 'contractorBalance']) || Map();
const contractorInventoryBalanceSelector = state => state.campaign.getIn(['ipfsCampaignSummary', 'inventory']) || Map();
const priceSelector = state => state.campaign.getIn(['campaign', 'token_price']);
const minimumContributionSelector = state => state.campaign.getIn(['campaign', 'minimum_investment'])
  || state.campaign.getIn(['campaign', 'min_donation']);
const plasmaSelector = state => (state.general.get('isBusinessMode')
  ? state.business.getIn(['businessDetails', 'business'])
  : state.user.get('userMetadata'));
const campaignSelector = state => state.campaign.get('campaign');

const getConversionsList = state => {
  const campaignType = state.campaign.getIn(['campaign', 'campaign_type']);
  return (state.campaign.getIn(['conversions', campaignType]) || List());
};
const getCampaignInfo = state => (state.campaign.get('campaign') || Map());
const getCampaignPreview = state => state.preview;
const getCampaignIpfsSummary = state => state.campaign.get('ipfsCampaignSummary');
const getKycList = state => state.campaign.get('kycList');
const getMaximumReferralReward = state => state.campaign.get('maximumReferralReward');
const getCountriesFromEnums = state => ((state.enums.get('enums').Country
  && state.enums.get('enums').Country.Country.name_to_value) || {});
const getIsUserJoined = state => state.campaign.get('isUserJoined');
const getConversionsPagination = state => state.campaign.get('conversionsPagination');
const getPendingActiveConversion = state => (state.campaign.get('isConversionExecuting')
  || state.campaign.get('isConversionSubmitting')
  || state.campaign.get('isConversionsFinalising'));
const getShortUrl = state => state.campaign.getIn(['campaign', 'short_url']);
const isCampaignRewardsBudgetInsufficientSelector = createSelector(
  getCampaignInfo,
  getCampaignIpfsSummary,
  (campaign, campaignIpfsSummary) => {
    let noRewards = campaign.get('no_referral_rewards')
      || campaign.get('incentive_model') === INCENTIVE_MODELS.NO_REFERRAL_REWARD;
    const campaignType = campaign.get('campaign_type');
    /**
     * Here we check that we have no enough budget for current conversion rewards
     */
    if (campaignIpfsSummary && campaignType === CAMPAIGN_TYPES.contentViews) {
      const bountyPerConversion = campaignIpfsSummary.getIn(['totalBounty', 'bountyPerConversion']);
      const availableInventory = campaignIpfsSummary.get('availableInventory');
      if (typeof bountyPerConversion === 'number' && typeof availableInventory === 'number') {
        console.log('NO_REWARDS', noRewards, availableInventory, bountyPerConversion);
        noRewards = noRewards || (availableInventory / bountyPerConversion < 1);
      }
    }

    return noRewards;
  }
);

export const CampaignSelectors = {
  isCampaignRewardsBudgetInsufficientSelector,
  isEnoughTokens: createSelector(
    inventorySelector,
    priceSelector,
    minimumContributionSelector,
    (inventory, price, min) => inventory.get('totalBalance') >= min / price
  ),
  campaignLinksSelector: createSelector(plasmaSelector, campaignSelector, (plasma, campaign) => (plasma
    ? {
      twokey_from_referrer_hash: walletManager.normalizeLinkFrom(campaign.get('twokey_from_referrer_hash')).rawLink,
      twokey_from_referrer_fsecret: walletManager.normalizeLinkFrom(campaign.get('twokey_from_referrer_hash')).fSecret,
      twokey_referrer_seed_hash: walletManager.normalizeLinkFrom(campaign.get('twokey_referrer_seed_hash')).rawLink,
      referral_hash: walletManager.normalizeLinkFrom(campaign.get('referral_hash')).rawLink,
      contractor_public_link_hash: walletManager.normalizeLinkFrom(campaign.get('contractor_public_link_hash')).rawLink,
      twokey_link: fSecretFromQuery.test(campaign.get('twokey_link'))
        ? `${window.location.origin}/${walletManager.normalizeLinkFrom(campaign.get('twokey_link')).rawLink}`
        : campaign.get('twokey_link'),
    }
    : {})),
  campaignShortUrlSelector: createSelector(plasmaSelector, getShortUrl, (plasma, shortUrl) =>
    (fSecretFromQuery.test(shortUrl)
      ? `${window.location.origin}/${walletManager.normalizeLinkFrom(shortUrl).rawLink}`
      : shortUrl)),
  selectConversionsList: createSelector(
    getConversionsList,
    getKycList,
    getCountriesFromEnums,
    getPendingActiveConversion,
    getConversionsPagination,
    (conversionsList, kycList, countries, pendingActiveConversion, pagination) => (
      conversionsList.map((conversion, index) => {
        const kyc = kycList.get(`${conversion.get('kyc_metadata_id')}`);
        const totalSize = pagination.getIn([conversion.get('campaign_type'), 'total_records']);

        return Map({
          campaignAddress: conversion.get('campaign_web3_address'),
          contribution: conversion.get('conversion_acquisition_amount'),
          currency: conversion.get('conversion_acquisition_currency'),
          date: new Date((conversion.get('transaction_timestamp') * 1000) || conversion.get('conversion_timestamp')),
          fiatContribution: conversion.get('conversion_acquisition_amount_converter_default_currency'),
          fiatCurrency: conversion.get('converter_default_currency'),
          id: conversion.get('id'),
          index: (totalSize || conversionsList.size) - index,
          isLoading: pendingActiveConversion === conversion.get('id'),
          referralsBeforeUser: conversion.get('n_referrers_to_converter'),
          status: getConversionStatusFromMap(conversion, kyc).status,
          tier: 'Tier 1',
          tokensSold: conversion.get('conversion_total_units_bought'),
          tokenSymbol: conversion.get('token_symbol'),
          txHash: conversion.get('final_execution_transaction_hash') || conversion.get('transaction_hash'),
          // userFullName: kyc && !conversion.get('hidden_profile') && `${kyc.get('first_name')} ${kyc.get('last_name')}`,
          // userHandle: (!conversion.get('hidden_profile') && conversion.get('converter_user_handle')),
          country: kyc && (countries[kyc.get('country')] || kyc.get('country')),
          userFullName: `${conversion.get('converter_user_first_name')} ${conversion.get('converter_user_last_name')}`,
          userHandle: conversion.get('converter_user_handle'),
          paid: conversion.get('ppc_conversion_payment_state') === 'PAID',
        });
      }))
  ),
  selectReferralRewards: createSelector(
    getCampaignInfo,
    getMaximumReferralReward,
    getIsUserJoined,
    isCampaignRewardsBudgetInsufficientSelector,
    getCampaignIpfsSummary,
    (campaignInfo, maximumReferralReward, isUserJoined, noRewards, summary) => {
      const incentiveType = maximumReferralReward === 0 ? INCENTIVE_MODELS.NO_REFERRAL_REWARD
        : campaignInfo.get('incentive_model') || INCENTIVE_MODELS.NO_REFERRAL_REWARD;
      const refMaxValue = maximumReferralReward || campaignInfo.get('twokey_max_referrer_cut_percent');
      const initMaxValue = campaignInfo.get('max_referral_cut_percent') || campaignInfo.get('max_referral_reward');
      const userCut = (campaignInfo.get('twokey_referrer_cut_percent') / 100) * refMaxValue || 0;
      const usdRate = summary.getIn(['rebalancing', 'priceAfterRebalancing'])
        || summary.getIn(['rebalancing', 'priceAtBeginning']) || summary.get('2keyUSDRate');

      return {
        arcsQuotaPerReferrer: campaignInfo.get('arcs_quota_per_referrer'),
        businessHandle: campaignInfo.get('business_handle'),
        campaignAddress: campaignInfo.get('campaign_web3_address'),
        campaignType: campaignInfo.get('campaign_type'),
        currency: campaignInfo.get('currency'),
        defaultValue: campaignInfo.get('incentive_model') === INCENTIVE_MODELS.MANUAL ? (refMaxValue / 2) : 0,
        incentiveType,
        isUserJoined,
        maximumReferralReward,
        maxValue: campaignInfo.get('incentive_model') === INCENTIVE_MODELS.MANUAL ? refMaxValue : initMaxValue,
        minValue: 0.1,
        mustConvertToRefer: campaignInfo.get('must_convert_to_refer'),
        refMaxValue,
        rewardsPerClick: (usdRate
          ? summary.getIn(['totalBounty', 'bountyPerConversion']) * usdRate
          : summary.getIn(['totalBounty', 'bountyPerConversion'])) || 0,
        '2keyUSDRate': usdRate,
        status: campaignInfo.get('status'),
        value: userCut,
        noRewards,
      };
    }
  ),
  selectCampaignInfo: createSelector(
    getCampaignInfo,
    getIsUserJoined,
    getCampaignPreview,
    (campaignInfo, isUserJoined, preview) =>
      fromJS(preview || {}).merge(campaignInfo).set('isUserJoined', isUserJoined),
  ),
  canArchiveSelector: createSelector(
    getCampaignInfo,
    contractorBalanceSelector,
    contractorInventoryBalanceSelector,
    getCampaignIpfsSummary,
    (campaign, contractorBalance, inventoryBalance, summary) => !(
      campaign.get('n_generated_actions_pending_approval') > 0
      || (campaign.get('status') !== CAMPAIGN_STATUS.ended && campaign.get('status') !== CAMPAIGN_STATUS.notActivated
        && campaign.get('status') !== CAMPAIGN_STATUS.expired)
      || inventoryBalance.get('totalBalance') || contractorBalance.get('available') || (
        campaign.get('campaign_type') === CAMPAIGN_TYPES.contentViews && summary.get('availableInventory')
      ))
  ),
  getCampaignClicksSummary: createSelector(getCampaignIpfsSummary, summary => {
    const budgetClicks = summary.getIn(['totalBounty', 'numberOfTotalPaidClicksSupported'])
      || Math.floor(summary.getIn(['totalBounty', 'totalBounty'])
      / summary.getIn(['totalBounty', 'bountyPerConversion']));
    const remainingClicks = summary.getIn(['totalBounty', 'numberOfPaidClicksLeft'])
      || Math.floor((summary.get('availableInventory')
      / (summary.getIn(['totalBounty', 'bountyPerConversion']) || 1)));
    return {
      remainingClicks,
      paidClicks: summary.getIn(['totalBounty', 'numberOfPaidClicksAchieved']) || (budgetClicks - remainingClicks),
    };
  }),
  isOpenPayItForwardModal,
};

export default CampaignSelectors;
